import { useEffect, useState } from 'react'
import { Card, Text, Title } from '@nimbus-ds/components'
import { RiskLevelSelector } from '@/components/experiments/RiskLevelSelector'
import { riskService } from '@/api/risk'
import { useToast } from '@/hooks/use-toast'
import type { RiskLevel } from '@/types'

interface RiskDefaultCardProps {
  storeId: string
}

/**
 * Store-level default for the risk control (RISK-LEVELS-COPY.md): the posture new experiments
 * pre-select. Saves on selection — a one-attribute store setting, no form to submit.
 */
export function RiskDefaultCard({ storeId }: RiskDefaultCardProps) {
  const { toast } = useToast()
  const [level, setLevel] = useState<RiskLevel>('balanced')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    riskService.getConfig(storeId).then((lvl) => {
      if (!cancelled) {
        setLevel(lvl)
        setLoading(false)
      }
    })
    return () => {
      cancelled = true
    }
  }, [storeId])

  const handleChange = async (newLevel: RiskLevel) => {
    const previous = level
    setLevel(newLevel)
    const ok = await riskService.saveConfig(storeId, newLevel)
    if (ok) {
      toast({
        title: 'Preferencia guardada',
        description: 'Los nuevos experimentos van a arrancar con este nivel preseleccionado.',
      })
    } else {
      setLevel(previous)
      toast({
        title: 'Error',
        description: 'No se pudo guardar la preferencia. Probá de nuevo.',
        variant: 'destructive',
      })
    }
  }

  return (
    <Card>
      <Card.Header>
        <Title as="h3">Nivel de riesgo por defecto</Title>
        <Text>
          El punto de partida para tus nuevos experimentos. Cada experimento puede ajustarlo al
          crearlo o editarlo.
        </Text>
      </Card.Header>
      <Card.Body>
        {loading ? (
          <Text>Cargando…</Text>
        ) : (
          <RiskLevelSelector
            value={level}
            onChange={handleChange}
            recommended="balanced"
            name="store-default-risk-level"
          />
        )}
      </Card.Body>
    </Card>
  )
}
